import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { getApi } from "../../../api/api";
import { setCity, selectCity } from "../../../store/slices/citySlice";
import { setCommonModalState } from "../../../store/slices/commonModalSlice";
import { clearCart } from "../../../store/slices/cartSlice";
import { clearComplexOrderCart } from "../../../store/slices/complexOrderCartSlice";
import { CommonModals } from "../../../common/types/enums";
import { CityInterface } from "../../../common/types/interfaces";
import { cityRoute } from "../../../common/utils/constants";

const OTHER_CITY: CityInterface = {
  id: 0,
  value: "Другой город",
  latitude: undefined,
  longitude: undefined,
};

export const useCityModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentCity = useSelector(selectCity);

  const [cities, setCities] = useState<CityInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    getApi()
      .getCities({ onlyWithPlaces: true, target: "logmall" })
      .then((data) => {
        if (!cancelled) setCities(data);
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = query.trim()
    ? cities.filter((c) =>
        c.value.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : cities;

  const baseList =
    query.trim() && filtered.length > 0
      ? filtered
      : [...filtered, OTHER_CITY];

  const sorted = [...baseList].sort((a, b) => {
    const activeId = currentCity?.id;
    const isActiveA = a.id === activeId;
    const isActiveB = b.id === activeId;

    if (isActiveA && !isActiveB) return -1;
    if (!isActiveA && isActiveB) return 1;
    return 0;
  });

  const clearSearch = useCallback(() => setQuery(""), []);

  const handleSelectCity = useCallback(
    (city: CityInterface) => {
      const isCityChanged = currentCity?.id !== city.id;

      if (isCityChanged) {
        dispatch(clearCart());
        dispatch(clearComplexOrderCart());
      }

      setQuery("");
      dispatch(setCity(city));
      dispatch(
        setCommonModalState({ modal: CommonModals.CITY_SELECT, state: false }),
      );

      navigate(cityRoute(city.id!), { replace: true });
    },
    [dispatch, currentCity?.id, navigate],
  );

  const closeModal = useCallback(() => {
    if (currentCity?.id !== undefined) {
      dispatch(
        setCommonModalState({ modal: CommonModals.CITY_SELECT, state: false }),
      );
    }
  }, [currentCity?.id, dispatch]);

  return {
    cities: sorted,
    loading,
    query,
    setQuery,
    clearSearch,
    selectCity: handleSelectCity,
    currentCity,
    closeModal,
    hasSelectedCity: currentCity?.id !== undefined,
  };
};